import { useMemo } from "react";
import { FONT, COLOR, RADIUS } from "../../lib/tokens";
import {
  SLOT_LABELS, MEAL_SLOTS, COST_TIER_LABELS,
  dayLabelFor, dayDateFor, slotKey,
} from "./plannerShared";

// WeeklyPlanReview — the proposed week as a 7-day menu, one card per
// filled slot. Presentational: every mutation (reroll, swap, lock,
// remove, commit, shopping) is a callback owned by WeeklyPlanner.
//
// Slot object shapes (see plan-week edge fn):
//   book:    { day, slot, source:"book", title, recipeSlug, reason }
//   concept: { day, slot, source:"new", title, concept:{...} }
//   error:   { day, slot, error:"allergen_conflict"|..., title? }

const kicker = {
  fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
  letterSpacing: "0.14em", color: COLOR.sub,
};

function MacroLine({ per }) {
  if (!per || (per.kcal == null && per.protein_g == null)) return null;
  const bits = [];
  if (per.kcal != null) bits.push(`~${per.kcal} kcal`);
  if (per.protein_g != null) bits.push(`${per.protein_g}g protein`);
  return (
    <span style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.sub }}>
      {bits.join(" · ")} <span style={{ opacity: 0.7 }}>est.</span>
    </span>
  );
}

function SlotCard({
  slotObj, weekStartISO, digestBySlug, busy, locked,
  onReroll, onToggleLock, onRemove,
}) {
  const isError = Boolean(slotObj.error);
  const isBook = slotObj.source === "book";
  const concept = slotObj.concept || null;
  const per = isBook
    ? digestBySlug.get(slotObj.recipeSlug)?.perServing || null
    : concept?.estPerServing || null;
  const minutes = isBook
    ? digestBySlug.get(slotObj.recipeSlug)?.timeMinutes || null
    : concept?.estMinutes || null;

  const actionBtn = (label, onClick, tone = COLOR.sub) => (
    <button
      className="mcm-focusable"
      onClick={onClick}
      disabled={busy}
      style={{
        fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
        letterSpacing: "0.1em", color: tone,
        background: "transparent", border: "none",
        padding: "6px 8px", cursor: busy ? "wait" : "pointer",
        opacity: busy ? 0.5 : 1,
      }}
    >
      {label}
    </button>
  );

  return (
    <div
      style={{
        background: COLOR.ground,
        border: `1px solid ${isError ? COLOR.roseDim : locked ? COLOR.goldDim : COLOR.edge}`,
        borderRadius: RADIUS.xl,
        padding: "14px 14px",
        marginBottom: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 8 }}>
        <span style={{ ...kicker, fontSize: 12 }}>{SLOT_LABELS[slotObj.slot]}</span>
        <span style={{
          fontFamily: FONT.forma, fontSize: 12, fontWeight: 600,
          letterSpacing: "0.08em",
          color: isError ? COLOR.rose : isBook ? COLOR.leaf : COLOR.gold,
        }}>
          {isError ? "NEEDS ATTENTION" : isBook ? "BOOK" : `NEW ${COST_TIER_LABELS[concept?.costTier] || ""}`}
        </span>
      </div>

      {isError ? (
        <div style={{ fontFamily: FONT.forma, fontSize: 13, color: COLOR.muted, margin: "6px 0" }}>
          {slotObj.error === "allergen_conflict"
            ? `"${slotObj.title || "The proposed dish"}" conflicts with an allergy — reroll it.`
            : "This slot didn't fill — reroll it."}
        </div>
      ) : (
        <>
          <div style={{
            fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500,
            fontSize: 17, color: COLOR.ink, margin: "4px 0 2px", lineHeight: 1.2,
          }}>
            {slotObj.title}
          </div>
          {(isBook ? slotObj.reason : concept?.subtitle) ? (
            <div style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.muted, marginBottom: 4 }}>
              {isBook ? slotObj.reason : concept.subtitle}
            </div>
          ) : null}
          <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
            <MacroLine per={per} />
            {minutes ? (
              <span style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.sub }}>
                {minutes} min
              </span>
            ) : null}
          </div>
        </>
      )}

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 2, marginTop: 4 }}>
        {!isError && actionBtn(locked ? "LOCKED" : "LOCK", onToggleLock, locked ? COLOR.gold : COLOR.sub)}
        {!locked && actionBtn(busy ? "…" : isBook && !isError ? "SWAP" : "REROLL", onReroll, COLOR.sky)}
        {!locked && !isError && actionBtn("REMOVE", onRemove, COLOR.rose)}
      </div>
    </div>
  );
}

export default function WeeklyPlanReview({
  weekStartISO,
  slots = [],
  weekNote = "",
  digest = [],
  busySlotKey = null,
  lockedKeys = new Set(),
  committed = false,
  committing = false,
  shoppingCount = null,
  shoppingAdded = false,
  onReroll,
  onToggleLock,
  onRemove,
  onCommit,
  onAddShopping,
  onBack,
  onDone,
}) {
  const digestBySlug = useMemo(
    () => new Map(digest.map((r) => [r.slug, r])),
    [digest],
  );

  const filled = slots.filter((s) => s && s.source && !s.error && s.enabled !== false);
  const errored = slots.filter((s) => s && s.error);

  // Summary strip — average per-day macros over slots that carry an
  // estimate, plus the week's cost-tier mix. All labelled "est."
  const summary = useMemo(() => {
    let kcal = 0, protein = 0, counted = 0;
    const tierMix = { 1: 0, 2: 0, 3: 0 };
    const days = new Set();
    for (const s of filled) {
      days.add(s.day);
      const per = s.source === "book"
        ? digestBySlug.get(s.recipeSlug)?.perServing
        : s.concept?.estPerServing;
      if (per?.kcal != null) { kcal += per.kcal; protein += per.protein_g || 0; counted++; }
      if (s.source === "new" && s.concept?.costTier) tierMix[s.concept.costTier]++;
    }
    const dayCount = Math.max(1, days.size);
    return {
      perDayKcal: counted ? Math.round(kcal / dayCount) : null,
      perDayProtein: counted ? Math.round(protein / dayCount) : null,
      coverage: counted,
      total: filled.length,
      tierMix,
    };
  }, [filled, digestBySlug]);

  // Group by day for rendering.
  const byDay = useMemo(() => {
    const map = new Map();
    for (const s of [...filled, ...errored]) {
      if (!map.has(s.day)) map.set(s.day, []);
      map.get(s.day).push(s);
    }
    for (const list of map.values()) {
      list.sort((a, b) => MEAL_SLOTS.indexOf(a.slot) - MEAL_SLOTS.indexOf(b.slot));
    }
    return [...map.entries()].sort((a, b) => a[0] - b[0]);
  }, [filled, errored]);

  const bigCta = (label, onClick, { disabled = false, tone = COLOR.gold } = {}) => (
    <button
      className="mcm-focusable"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        fontFamily: FONT.forma, fontSize: 14, fontWeight: 600,
        letterSpacing: "0.12em", color: COLOR.ink,
        background: disabled ? COLOR.deep : tone === COLOR.gold ? COLOR.goldDeep : COLOR.skyDeep,
        border: `1px solid ${disabled ? COLOR.border : tone === COLOR.gold ? COLOR.goldDim : COLOR.skyBorder}`,
        borderRadius: RADIUS.xl,
        padding: "14px 16px",
        cursor: disabled ? "default" : "pointer",
        opacity: disabled ? 0.6 : 1,
        marginTop: 8,
      }}
    >
      {label}
    </button>
  );

  return (
    <div>
      <div style={{ ...kicker, color: COLOR.gold, marginBottom: 4 }}>YOUR WEEK, PLANNED</div>
      <div style={{
        fontFamily: FONT.itemTitle, fontStyle: "italic", fontWeight: 500,
        fontSize: 24, color: COLOR.ink, marginBottom: 6, lineHeight: 1.15,
      }}>
        {filled.length} meal{filled.length === 1 ? "" : "s"} across the week
      </div>
      {weekNote ? (
        <div style={{ fontFamily: FONT.forma, fontSize: 13, color: COLOR.muted, marginBottom: 10 }}>
          {weekNote}
        </div>
      ) : null}

      {/* Summary strip */}
      <div style={{
        display: "flex", gap: 14, flexWrap: "wrap",
        background: COLOR.soil, border: `1px solid ${COLOR.edge}`,
        borderRadius: RADIUS.lg, padding: "12px 14px", marginBottom: 20,
      }}>
        {summary.perDayKcal != null && (
          <div>
            <div style={kicker}>PER DAY (EST.)</div>
            <div style={{ fontFamily: FONT.forma, fontSize: 14, fontWeight: 600, color: COLOR.ink }}>
              ~{summary.perDayKcal} kcal · {summary.perDayProtein}g protein
            </div>
          </div>
        )}
        <div>
          <div style={kicker}>COST FEEL (EST.)</div>
          <div style={{ fontFamily: FONT.forma, fontSize: 14, fontWeight: 600, color: COLOR.ink }}>
            {[1, 2, 3]
              .filter((t) => summary.tierMix[t] > 0)
              .map((t) => `${summary.tierMix[t]}× ${COST_TIER_LABELS[t]}`)
              .join("  ") || "book picks"}
          </div>
        </div>
        {summary.coverage < summary.total && (
          <div style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.sub, alignSelf: "flex-end" }}>
            macros known for {summary.coverage} of {summary.total}
          </div>
        )}
      </div>

      {/* 7-day menu */}
      {byDay.map(([day, list]) => (
        <div key={day} style={{ marginBottom: 22 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
            <span style={{ ...kicker, color: COLOR.ink, fontSize: 13 }}>
              {dayLabelFor(weekStartISO, day)}
            </span>
            <span style={{ fontFamily: FONT.forma, fontSize: 12, color: COLOR.sub }}>
              {dayDateFor(weekStartISO, day)}
            </span>
          </div>
          {list.map((s) => (
            <SlotCard
              key={slotKey(s)}
              slotObj={s}
              weekStartISO={weekStartISO}
              digestBySlug={digestBySlug}
              busy={busySlotKey === slotKey(s)}
              locked={lockedKeys.has(slotKey(s))}
              onReroll={() => onReroll?.(s)}
              onToggleLock={() => onToggleLock?.(s)}
              onRemove={() => onRemove?.(s)}
            />
          ))}
        </div>
      ))}

      {/* Footer CTAs */}
      {!committed ? (
        <>
          {bigCta(
            committing ? "ADDING TO CALENDAR…" : `ADD ${filled.length} MEALS TO CALENDAR`,
            onCommit,
            { disabled: committing || filled.length === 0 },
          )}
          <button
            onClick={onBack}
            style={{
              width: "100%", background: "transparent", border: "none",
              fontFamily: FONT.forma, fontSize: 13, color: COLOR.sub,
              letterSpacing: "0.08em", padding: "12px 0", cursor: "pointer",
            }}
          >
            BACK TO SETUP
          </button>
        </>
      ) : (
        <>
          <div style={{
            fontFamily: FONT.forma, fontSize: 13, color: COLOR.leaf,
            fontWeight: 600, textAlign: "center", margin: "10px 0 2px",
          }}>
            Week added to your calendar.
          </div>
          {shoppingCount == null || shoppingCount > 0
            ? bigCta(
                shoppingAdded
                  ? "SHOPPING LIST UPDATED"
                  : shoppingCount == null
                    ? "ADD MISSING TO SHOPPING LIST"
                    : `ADD ${shoppingCount} MISSING ITEM${shoppingCount === 1 ? "" : "S"} TO SHOPPING LIST`,
                onAddShopping,
                { disabled: shoppingAdded, tone: COLOR.sky },
              )
            : (
              <div style={{ fontFamily: FONT.forma, fontSize: 13, color: COLOR.sub, textAlign: "center", margin: "8px 0" }}>
                Your pantry covers the whole week — nothing to buy.
              </div>
            )}
          {bigCta("DONE", onDone)}
        </>
      )}
    </div>
  );
}
